import React, { useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store/rootState'
import { Icon, Button, Dropdown, Popup, Label, Image } from 'semantic-ui-react'
import { ContentHeader } from '../../../components/Header/ContentHeader'
import budget_details from '../../../assets/svg/budget_details.svg'
import data_analytics from '../../../assets/svg/data_analytics.svg'
import data_analytics_active from '../../../assets/svg/data_analytics_active.svg'
import draft from '../../../assets/svg/draft.svg'
import accepted from '../../../assets/svg/accepted.svg'
import rejected from '../../../assets/svg/rejected.svg'
import git_like_icon from '../../../assets/svg/git_like_icon.svg'
import DeleteBudgetConfirmationModal from '../components/DeleteBudgetConfirmationModal'
import ConfirmDeleteModal from '../../../components/Modal/ConfirmDeleteModal'
import { resolveEditToggle } from '../utils/editToggle'
import {
  selectBudgetDetail,
  selectBudgetDetailEditMode,
  selectBudgetDetailError,
  selectBudgetDetailViewType,
  setBudgetError,
  clearBudgetError,
  setViewType,
  clearBudgetDetail,
  setEditMode
} from '../reducers/budgetDetailReducer'
import {
  toggleEditMode,
  exportBudgetCSV,
  exportBudgetPDF,
  fetchBudgetDetail
} from '../actions/budgetDetailActions'
import { deleteBudget } from '../actions/budgetActions'
import { saveBudget } from '../actions/budgetSaveActions'
import { selectHasChanges, selectChanges, resetChanges, setOriginalData } from '../slices/changeTrackerSlice'
import { previewRecalculation } from '../../../store/budget/budgetProjectSlice'
import BudgetSummaryWidget from '../components/BudgetSummaryWidget'
import PendingCalculationsModal from '../components/PendingCalculationsModal'
import { getBudgetClientName } from '../utils/clientNameResolver'
import { getClients } from '../actions/budgetSettingsActions'
import { budgetApi, IProjectBudget } from '../../../api/budgetApi'
import DeclineBudgetConfirmationModal from '../components/DeclineBudgetConfirmationModal'
import { useNotificationContext } from '../../../components/Toast/NotificationProvider'
import { extractErrorMessage } from '../../../utils/errorHandler'
import '../components/Buttons.css'

interface VersionOption {
  key: string | number
  text: string
  value: string | number
  /** The bare budget name (no "(Initial)" suffix), used elsewhere in the header. */
  name: string
  /** True for the original accepted budget (source of amendments); false for amendments. */
  isOriginal: boolean
}

interface RouteParams {
  id: string
}

/**
 * Container component for BudgetDetailHeader that connects to Redux
 * Uses ContentHeader pattern matching project pages (projectActivitiesHeader)
 * Budget widget in rightItemsFirst, action buttons in rightItemsSecond
 */
const BudgetDetailHeaderContainer: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const dispatch = useDispatch()
  const { showNotification } = useNotificationContext()
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)
  const [showDeclineConfirm, setShowDeclineConfirm] = React.useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false)
  const [isOptionsOpen, setIsOptionsOpen] = React.useState(false)
  const [showPendingCalcModal, setShowPendingCalcModal] = React.useState(false)
  const [pendingCalcCount, setPendingCalcCount] = React.useState(0)

  const budget = useSelector(selectBudgetDetail)
  const editMode = useSelector(selectBudgetDetailEditMode)
  const hasChanges = useSelector(selectHasChanges)
  const trackedChanges = useSelector(selectChanges)
  const viewType = useSelector(selectBudgetDetailViewType)
  const clients = useSelector((state: RootState) => state.budgetSettings.clients.clients)

  const [versions, setVersions] = React.useState<VersionOption[]>([])

  const isAccepted = budget?.status === 'accepted'

  useEffect(() => {
    dispatch(getClients() as any)
  }, [dispatch])

  // Fetch the whole amendment family for this budget (root + every amendment at
  // any depth). The backend walks the full family from any member id.
  useEffect(() => {
    if (!budget) return
    let cancelled = false
    budgetApi.getBudgetVersions(budget.id as any)
      .then((items: IProjectBudget[]) => {
        if (cancelled) return
        const sorted = [...items].sort((a, b) => {
          // Root first, then amendments by created_at ascending
          const aIsRoot = !a.source_budget
          const bIsRoot = !b.source_budget
          if (aIsRoot !== bIsRoot) return aIsRoot ? -1 : 1
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        })
        const options: VersionOption[] = sorted.map(v => ({
          key: v.id,
          value: v.id,
          text: v.source_budget ? v.name : `${v.name} (Initial)`,
          name: v.name,
          isOriginal: !v.source_budget,
        }))
        setVersions(options)
      })
      .catch(err => {
        if (!cancelled) console.error('Failed to load budget versions', err)
      })
    return () => { cancelled = true }
  }, [budget?.id, budget?.source_budget])

  const handleVersionChange = (_: any, data: any) => {
    const selected = data.value
    if (!selected || !budget) return
    if (selected === budget.id) return
    history.push(`/budget/${selected}/detail`)
  }

  /** Wipe all edit state and refetch fresh data from the API. */
  const discardEdits = () => {
    dispatch(clearBudgetDetail() as any)
    dispatch(resetChanges() as any)
    dispatch(setEditMode(false) as any)
    if (id) {
      dispatch(fetchBudgetDetail(id, true) as any) // forceRefresh = true
    }
  }

  const handleEditToggle = () => {
    const action = resolveEditToggle(editMode, hasChanges)
    if (action === 'enter') {
      dispatch(toggleEditMode() as any)
    } else if (action === 'confirm') {
      setShowCancelConfirm(true) // ask before discarding unsaved changes
    } else {
      discardEdits() // no unsaved changes — close immediately
    }
  }

  /** Resolve the discard-changes confirmation: 'delete' = discard, 'cancel' = keep editing. */
  const handleCancelConfirm = (action: 'cancel' | 'delete') => {
    setShowCancelConfirm(false)
    if (action === 'delete') {
      discardEdits()
    }
  }

  const handleSave = async () => {
    if (!budget) return

    const budgetId = typeof budget.id === 'string' ? parseInt(budget.id, 10) : budget.id

    // Check for pending calculations before saving
    const previewResult = await dispatch(previewRecalculation(budgetId) as any)

    if (previewResult.type.endsWith('fulfilled') && previewResult.payload?.changes?.length > 0) {
      // There are pending calculations - show warning modal
      setPendingCalcCount(previewResult.payload.changes.length)
      setShowPendingCalcModal(true)
      return
    }

    // No pending calculations - proceed with save
    await performSave()
  }

  const performSave = async () => {
    if (!budget) return
    try {
      dispatch(clearBudgetError() as any)
      const budgetId = typeof budget.id === 'string' ? parseInt(budget.id, 10) : budget.id
      await dispatch(saveBudget(budgetId) as any)
      setShowPendingCalcModal(false)
    } catch (error: any) {
      console.error('[BudgetDetailHeaderContainer] Save failed:', error)
      const errorMessage = error.message || 'Failed to save budget'
      dispatch(setBudgetError(errorMessage) as any)
    }
  }

  const handlePendingCalcCancel = () => {
    setShowPendingCalcModal(false)
  }

  const handlePendingCalcCalculate = () => {
    setShowPendingCalcModal(false)
    // Trigger Calculate button click programmatically
    // This will open the calculation dialog
    const calculateButton = document.querySelector('[data-testid="quick-access-calculate"]') as HTMLButtonElement
    calculateButton?.click()
  }

  const handlePendingCalcSaveAnyway = async () => {
    await performSave()
  }

  const handleDeleteClick = () => {
    setIsOptionsOpen(false)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false)
    if (!budget) return
    try {
      const budgetId = typeof budget.id === 'string' ? parseInt(budget.id, 10) : budget.id
      await dispatch(deleteBudget(budgetId) as any)
      history.push('/budget')
    } catch (error: any) {
      console.error('Failed to delete budget:', error)
      const responseData = error?.response?.data
      if (responseData?.code === 'has_amendments') {
        dispatch(setBudgetError(responseData.detail) as any)
      } else {
        dispatch(setBudgetError(responseData?.detail || 'Failed to delete budget') as any)
      }
    }
  }

  /** Re-fetch the detail so a status change is reflected immediately. */
  const refreshBudgetDetail = () => {
    if (id) dispatch(fetchBudgetDetail(id, true) as any)
  }

  /**
   * Run a lifecycle status change (accept / decline / move to draft), then
   * refresh the detail and toast the outcome. The backend enforces the 100%
   * completion and single-active-amendment rules, surfaced here as an error.
   */
  const runStatusChange = async (run: () => Promise<unknown>, successTitle: string) => {
    if (!budget) return
    setIsOptionsOpen(false)
    try {
      await run()
      refreshBudgetDetail()
      showNotification({ type: 'success', title: successTitle, message: `"${budget.name}" was updated.`, duration: 3000 })
    } catch (error: any) {
      showNotification({ type: 'error', title: 'Update failed', message: extractErrorMessage(error), duration: 5000 })
    }
  }

  const handleAccept = () => runStatusChange(() => budgetApi.acceptBudget(Number(budget!.id)), 'Budget accepted')

  const handleMoveToDraft = () => runStatusChange(() => budgetApi.updateBudgetStatus(budget!.id, 'draft'), 'Moved to draft')

  const handleDeclineClick = () => {
    setIsOptionsOpen(false)
    setShowDeclineConfirm(true)
  }

  const handleDeclineConfirm = () => {
    setShowDeclineConfirm(false)
    runStatusChange(() => budgetApi.updateBudgetStatus(budget!.id, 'declined'), 'Budget declined')
  }

  const handleExportCSV = async () => {
    setIsOptionsOpen(false)
    if (id) {
      try {
        await dispatch(exportBudgetCSV(id) as any)
      } catch (error) {
        console.error('Failed to export budget:', error)
        // Error is already logged by the action, could show toast notification here
      }
    }
  }

  const handleExportPDF = async () => {
    setIsOptionsOpen(false)
    if (id) {
      try {
        await dispatch(exportBudgetPDF(id) as any)
      } catch (error) {
        console.error('Failed to export budget PDF:', error)
      }
    }
  }

  const handleViewTypeChange = (active: 'list' | 'timeline') => {
    if (viewType !== active) {
      dispatch(setViewType(active))
    }
  }

  const getStatusColor = (status: string): any => {
    switch (status) {
      case 'draft':
        return 'grey'
      case 'accepted':
        return 'green'
      case 'declined':
        return 'red'
      default:
        return 'grey'
    }
  }

  const formatStatus = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1)
  }

  const getStatusIcon = (status: string): string | null => {
    switch (status) {
      case 'draft':
        return draft
      case 'accepted':
        return accepted
      case 'declined':
        return rejected
      default:
        return null
    }
  }

  const renderBudgetWidget = (): JSX.Element | undefined => {
    if (!budget) return undefined
    // Read live rebate from change tracker for real-time total preview
    const liveRebateValue = trackedChanges.rebate !== undefined
      ? String(trackedChanges.rebate || '')
      : ''
    return (
      <BudgetSummaryWidget
        budget={budget}
        editMode={editMode && !isAccepted}
        inputValueRebate={liveRebateValue}
      />
    )
  }

  const renderBottomActionBar = (): JSX.Element | undefined => {
    if (!budget) return undefined

    // Lifecycle actions (Accept / Decline / Move to Draft) mirror the budget list.
    // Shown only to editors (can_edit); the backend enforces the transition rules.
    const isComplete = budget.data_completeness_percentage === 100
    const renderStatusActions = () => {
      if (!budget.can_edit) return null
      return (
        <>
          {(budget.status === 'draft' || budget.status === 'declined') && isComplete && (
            <Dropdown.Item text="Accept" icon="check" onClick={handleAccept} />
          )}
          {(budget.status === 'draft' || budget.status === 'accepted') && (
            <Dropdown.Item text="Decline" icon="times" onClick={handleDeclineClick} />
          )}
          {(budget.status === 'accepted' || budget.status === 'declined') && (
            <Dropdown.Item text="Move to Draft" icon="undo" onClick={handleMoveToDraft} />
          )}
          <Dropdown.Divider />
        </>
      )
    }

    return (
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        paddingTop: '15px',
        height: '50px'
      }}>
        <div style={{ display: 'flex', gap: '8px', marginRight: 'auto' }}>
          <button
            onClick={() => handleViewTypeChange('list')}
            style={{
              width: '32px',
              height: '32px',
              boxShadow: viewType === 'list'
                ? '0 0 0 1px rgba(8, 214, 161, 1)'
                : '0 0 0 1px rgba(190, 200, 195, 1)',
              borderRadius: '7.2px',
              backgroundColor: 'rgba(255, 255, 255, 1)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
          >
            <Image
              src={budget_details}
              style={{
                width: 'auto',
                height: 'auto',
                filter: viewType === 'list'
                  ? 'none'
                  : 'brightness(0) saturate(100%) invert(86%) sepia(6%) saturate(342%) hue-rotate(99deg) brightness(93%) contrast(90%)'
              }}
            />
          </button>
          <button
            onClick={() => handleViewTypeChange('timeline')}
            style={{
              width: '32px',
              height: '32px',
              boxShadow: viewType === 'timeline'
                ? '0 0 0 1px rgba(8, 214, 161, 1)'
                : '0 0 0 1px rgba(190, 200, 195, 1)',
              borderRadius: '7.2px',
              backgroundColor: 'rgba(255, 255, 255, 1)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0
            }}
          >
            {/* Swap the asset rather than filter it. The active green used to be
                synthesised from this icon's grey with a hue-rotate chain, which lands
                near #08D6A1 but not on it — so the glyph never matched the button's own
                rgba(8,214,161,1) border (issue #119). The list button beside this one
                needs no filter because its asset is already that green; this one now
                has a matching variant, the same pairing the activities module uses for
                act_list_view / act_list_view_active. */}
            <Image
              src={viewType === 'timeline' ? data_analytics_active : data_analytics}
              style={{ width: 'auto', height: 'auto' }}
            />
          </button>
        </div>
        {viewType === 'list' && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {budget.can_edit ? (
              <>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Dropdown
                    button
                    className="budget-button budget-button--primary budget-phase-dropdown"
                    data-testid="new-dropdown-before-menu"
                    value={budget.id}
                    options={versions.length > 0
                      ? versions.map(v => ({ key: v.key, text: v.text, value: v.value }))
                      : [{ key: 'initial-offer', text: 'Initial Offer', value: budget.id }]}
                    onChange={handleVersionChange}
                    style={{
                      width: '180px',
                      height: '32px',
                      boxShadow: '0 0 0 1px rgba(190, 200, 195, 1)',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      color: 'rgba(116, 118, 120, 1)',
                      fontSize: '14px',
                      fontWeight: 500,
                      letterSpacing: '0.22px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 1em 0 2.5em'
                    }}
                  />
                  <img
                    src={git_like_icon}
                    alt="Version icon"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '20px',
                      pointerEvents: 'none'
                    }}
                  />
                  <Icon
                    name="dropdown"
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'rgba(8, 214, 161, 1)',
                      margin: 0,
                      lineHeight: 1,
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      pointerEvents: 'none'
                    }}
                  />
                </div>
                <Dropdown
                  icon="ellipsis horizontal"
                  floating
                  button
                  className="icon budget-menu-button"
                  open={isOptionsOpen}
                  onOpen={() => setIsOptionsOpen(true)}
                  onClose={() => setIsOptionsOpen(false)}
                  data-testid="options-dropdown-trigger"
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'rgba(35, 44, 88, 1)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                >
                  <Dropdown.Menu>
                    {renderStatusActions()}
                    <Dropdown.Item text="Export CSV" icon="download" onClick={handleExportCSV} />
                    <Dropdown.Item text="Export PDF" icon="file pdf" onClick={handleExportPDF} />
                    {budget.can_delete && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item text="Delete" icon="trash" onClick={handleDeleteClick} />
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                {editMode && (
                  <button
                    className="budget-button budget-button--primary budget-button--icon"
                    onClick={handleSave}
                    disabled={!hasChanges}
                    data-testid="save-budget-button"
                  >
                    Save
                  </button>
                )}
                {isAccepted ? (
                  <Popup
                    content="Cannot edit an accepted budget. Change status to Draft first."
                    trigger={
                      <span style={{ display: 'inline-block' }}>
                        <button
                          className="budget-button budget-button--primary budget-button--icon"
                          disabled
                          data-testid="edit-budget-button"
                          style={{ pointerEvents: 'none', opacity: 0.5 }}
                        >
                          Edit
                        </button>
                      </span>
                    }
                    position="bottom center"
                    inverted
                    size="small"
                  />
                ) : (
                  <button
                    className={`budget-button ${editMode ? 'budget-button--cancel' : 'budget-button--primary'} budget-button--icon`}
                    onClick={handleEditToggle}
                    data-testid="edit-budget-button"
                  >
                    {editMode ? 'Cancel' : 'Edit'}
                  </button>
                )}
              </>
            ) : (
              <>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Dropdown
                    button
                    className="budget-button budget-button--primary budget-phase-dropdown"
                    data-testid="new-dropdown-before-menu"
                    value={budget.id}
                    options={versions.length > 0
                      ? versions.map(v => ({ key: v.key, text: v.text, value: v.value }))
                      : [{ key: 'initial-offer', text: 'Initial Offer', value: budget.id }]}
                    onChange={handleVersionChange}
                    style={{
                      width: '180px',
                      height: '32px',
                      boxShadow: '0 0 0 1px rgba(190, 200, 195, 1)',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      color: 'rgba(116, 118, 120, 1)',
                      fontSize: '14px',
                      fontWeight: 500,
                      letterSpacing: '0.22px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 1em 0 2.5em'
                    }}
                  />
                  <img
                    src={git_like_icon}
                    alt="Version icon"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '20px',
                      pointerEvents: 'none'
                    }}
                  />
                  <Icon
                    name="dropdown"
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'rgba(8, 214, 161, 1)',
                      margin: 0,
                      lineHeight: 1,
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      pointerEvents: 'none'
                    }}
                  />
                </div>
                <Dropdown
                  icon="ellipsis horizontal"
                  floating
                  button
                  className="icon budget-menu-button"
                  open={isOptionsOpen}
                  onOpen={() => setIsOptionsOpen(true)}
                  onClose={() => setIsOptionsOpen(false)}
                  data-testid="options-dropdown-trigger"
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'rgba(35, 44, 88, 1)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                >
                  <Dropdown.Menu>
                    {renderStatusActions()}
                    <Dropdown.Item text="Export CSV" icon="download" onClick={handleExportCSV} />
                    <Dropdown.Item text="Export PDF" icon="file pdf" onClick={handleExportPDF} />
                    {budget.can_delete && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item text="Delete" icon="trash" onClick={handleDeleteClick} />
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                <Popup
                  trigger={
                    <span>
                      <button
                        className="budget-button budget-button--cancel budget-button--icon"
                        disabled
                        data-testid="edit-budget-button-disabled"
                        style={{ pointerEvents: 'auto' }}
                      >
                        Edit
                      </button>
                    </span>
                  }
                  content="You don't have permission to edit this budget. Only the project manager or creator can edit."
                  position="bottom center"
                  inverted
                  size="small"
                />
              </>
            )}
          </div>
        )}

        <DeleteBudgetConfirmationModal
          open={showDeleteConfirm}
          budgetName={budget.name}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          isDeleting={false}
        />

        <DeclineBudgetConfirmationModal
          open={showDeclineConfirm}
          budgetName={budget.name}
          onClose={() => setShowDeclineConfirm(false)}
          onConfirm={handleDeclineConfirm}
          isDeclining={false}
        />

        <ConfirmDeleteModal
          open={showCancelConfirm}
          confirmUpdateModal={true}
          confirmUpdateCustomTitle="Discard changes?"
          confirmUpdateCustomMsg="You have unsaved changes. Are you sure you want to discard them?"
          userInput={handleCancelConfirm}
        />

        <PendingCalculationsModal
          open={showPendingCalcModal}
          changesCount={pendingCalcCount}
          onCancel={handlePendingCalcCancel}
          onCalculate={handlePendingCalcCalculate}
          onSaveAnyway={handlePendingCalcSaveAnyway}
        />
      </div>
    )
  }

  if (!budget || !clients || clients.length === 0) {
    return null
  }

  const clientName = getBudgetClientName(budget, clients)

  const originalVersion = versions.find(v => v.isOriginal)
  const originalName = originalVersion?.name || budget.name
  const isAmendment = Boolean(budget.source_budget)
  const titleNode: string | JSX.Element = isAmendment ? (
    <span data-testid="amendment-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
      {originalName}
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '18px',
        padding: '0 8px',
        fontSize: '12px',
        fontFamily: 'Roboto-Regular, Roboto, sans-serif',
        color: 'rgba(47, 97, 213, 1)',
        backgroundColor: 'rgba(234, 239, 251, 1)',
        border: '0.8px solid rgba(47, 97, 213, 1)',
        borderRadius: '6.4px',
        letterSpacing: '0.25px',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      }}>
        {budget.name}
      </span>
    </span>
  ) : budget.name

  return (
    <div>
      <ContentHeader
        id="projectActivitiesHeader"
        title={titleNode}
        color="#08D6A1"
        headerItems={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{
              fontSize: '20.25px',
              fontWeight: 400,
              letterSpacing: '0.25px'
            }}>{clientName}</span>
            {isAmendment && originalVersion && (
              <span
                onClick={() => history.push(`/budget/${originalVersion.value}/detail`)}
                data-testid="amendment-source-link"
                role="link"
                title="Go to the initial budget this amendment was created from"
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: '30px', cursor: 'pointer' }}
              >
                <Icon name="linkify" style={{ color: 'rgba(8, 214, 161, 1)', margin: 0, fontSize: '14px', height: '14px', lineHeight: 1, verticalAlign: 'middle', display: 'inline-block' }} />
                <span style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  letterSpacing: '0.25px'
                }}>{originalVersion.name} - Initial</span>
              </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: '30px' }}>
              {getStatusIcon(budget.status) && (
                <Image src={getStatusIcon(budget.status)!} style={{ height: budget.status === 'declined' ? '15px' : '20px', width: 'auto' }} />
              )}
              <span style={{
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '0.25px'
              }}>{formatStatus(budget.status)}</span>
            </span>
          </div>
        }
        backTitle="Budget List"
        backLocation="/budget"
        rightItemsFirst={viewType === 'list' ? renderBudgetWidget() : undefined}
        column={true}
      />
      {renderBottomActionBar()}
    </div>
  )
}

export default BudgetDetailHeaderContainer
